// Display-only currency helper for the pricing page.
// Paddle handles the actual charge and conversion at checkout — these values
// are approximations to reduce friction for UK/EU visitors.

import { detectTimezone } from "./cookie-consent";

export type Currency = "USD" | "GBP" | "EUR";

export const CURRENCY_SYMBOL: Record<Currency, string> = {
  USD: "$",
  GBP: "£",
  EUR: "€",
};

export function detectCurrency(tz: string = detectTimezone()): Currency {
  if (!tz) return "USD";
  if (tz === "Europe/London") return "GBP";
  if (tz.startsWith("Europe/")) return "EUR";
  return "USD";
}

// Pre-computed per-currency price strings (spec-exact).
type PriceRow = {
  priceMain: string;
  priceCadence: string;
  subline?: string;
};

export const PRICING_TABLE: Record<
  "free" | "householdAnnual" | "householdMonthly" | "proAnnual" | "proMonthly" | "lifetime",
  Record<Currency, PriceRow>
> = {
  free: {
    USD: { priceMain: "$0", priceCadence: "forever" },
    GBP: { priceMain: "£0", priceCadence: "forever" },
    EUR: { priceMain: "€0", priceCadence: "forever" },
  },
  householdAnnual: {
    USD: { priceMain: "$3.33", priceCadence: "/mo billed annually", subline: "$39.99 per year" },
    GBP: { priceMain: "£2.63", priceCadence: "/mo billed annually", subline: "£31.59 per year" },
    EUR: { priceMain: "€3.07", priceCadence: "/mo billed annually", subline: "€36.79 per year" },
  },
  householdMonthly: {
    USD: { priceMain: "$4.99", priceCadence: "/ month", subline: "Billed monthly" },
    GBP: { priceMain: "£3.94", priceCadence: "/ month", subline: "Billed monthly" },
    EUR: { priceMain: "€4.59", priceCadence: "/ month", subline: "Billed monthly" },
  },
  proAnnual: {
    USD: { priceMain: "$9.92", priceCadence: "/mo billed annually", subline: "$119 per year" },
    GBP: { priceMain: "£7.83", priceCadence: "/mo billed annually", subline: "£94.01 per year" },
    EUR: { priceMain: "€9.12", priceCadence: "/mo billed annually", subline: "€109.48 per year" },
  },
  proMonthly: {
    USD: { priceMain: "$14.99", priceCadence: "/ month", subline: "Billed monthly" },
    GBP: { priceMain: "£11.84", priceCadence: "/ month", subline: "Billed monthly" },
    EUR: { priceMain: "€13.79", priceCadence: "/ month", subline: "Billed monthly" },
  },
  lifetime: {
    USD: { priceMain: "$79", priceCadence: "one-time" },
    GBP: { priceMain: "£62.41", priceCadence: "one-time" },
    EUR: { priceMain: "€72.68", priceCadence: "one-time" },
  },
};

export const APPROX_NOTE = (c: Currency) =>
  c === "USD" ? undefined : `Billed in USD · ${c} price is approximate`;
